// core
import React, { useState } from "react";
import cx from "classnames";
import { PaymentFrequency } from "../../../../../common/interfaces/enums/PaymentFrequency";
import InsurerLogo from "../../../../../common/components/InsurerLogo/InsurerLogo";
import HeaderBreakdown from "../../Components/HeaderBreakdown";
import HeaderCashback from "../../Components/HeaderCashback";
import QuoteCallToAction from "../../Components/QuoteCallToAction/QuoteCallToAction";
import Premium from "../../../Premium/Premium";
import IQuoteCardResult from "../../../../interfaces/results/IQuoteCardResult";
import { IClient } from "../../../../../common/interfaces/IClient";
import QuoteTableState from "../../../../types/quoteTable/IQuoteTableState";
import styles from "./QuoteTableOverview.module.scss";
import QuoteTableOffersAndBenefitsContent from "../Cells/QuoteTableOffersAndBenefitsContent/QuoteTableOffersAndBenefitsContent";
import { QuoteTableView } from "../../../../enums/QuoteTableView";
import Button from "../../../../../common/components/Forms/Button/Button";
import { QuoteTableCallToActionFunction } from "../../../../hooks/useQuoteSections";
import { useHistory } from "react-router-dom";
import useCart from "../../../../../common/hooks/useCart";
import { ClientProductTypesList } from "../../../../../common/interfaces/enums/ProductType";
import { getProductTypeAsString } from "../../../../../common/shared/enumHelpers";
import Icon from "../../../../../common/components/Icon/Icon";
import QuoteResultError from "../../../Modals/QuoteResultErrorModal/QuoteResultError";

// interfaces
interface IQuoteTableHeaderContentProps {
    groupId: string;
    bannerHeadingText: string;
    card: IQuoteCardResult;
    clients: IClient[];
    handleCallToAction: QuoteTableCallToActionFunction;
    handlePaymentFrequencyChange: (newPaymentFrequency: PaymentFrequency) => void;
    setView: (newView: QuoteTableView) => void;
    quoteState: QuoteTableState;
}

// rendering
const QuoteTableOverviewCard: React.FC<IQuoteTableHeaderContentProps> = (props) => {
    const {
        groupId,
        bannerHeadingText,
        card,
        clients,
        handlePaymentFrequencyChange,
        setView,
        quoteState
    } = props;

    const history = useHistory();
    const {
        totalCashback,
        result: { totalPremium, paymentFrequency, providerId },
        state: { hasBlockingError }
    } = card;

    const { generateApplicationURL } = useCart();

    const [isOpen, setIsOpen] = useState(false);

    const productList = card.result.settings.map((s) => s.productType);

    return (
        <div
            className={cx([
                styles["basic-panel"],
                "layout-flex-column",
                "text-feature",
                {
                    [styles["is-highlighted"]]: quoteState.isHighlighted,
                    ["panel-quote-disabled"]: hasBlockingError,
                    [styles["is-disabled"]]: providerId === 5 && card.productInfo.life //this is a temporary solution until we can have react state reflect AdminViewOnly 
                }
            ])} 
            data-provider-id={providerId}>
            <div
                className={cx([
                    "layout-column-banner",
                    "panel-best-choice",
                    "text-banner",
                    {
                        "text-banner-hidden": !bannerHeadingText,
                        "layout-hidden-mobile": !quoteState.isHighlighted
                    }
                ])}>
                {bannerHeadingText}
            </div>

            <Premium
                totalPremium={card.result.totalPremium}
                size={"extra-large"}
                paymentFrequency={paymentFrequency}
                isBelowMinimumPremium={!quoteState.isOverMinimumPremium}
                onPaymentFrequencyChange={handlePaymentFrequencyChange}
                useFullFrequencyLabel
                hasBlockingError={hasBlockingError}
                className="gap-m"
            />

            {!hasBlockingError && (
                <React.Fragment>
                    <HeaderBreakdown
                        card={card}
                        clients={clients}
                        hasDiscountApplied={card.result.benefits.some(
                            (b) => b.discounts.length > 0
                        )}
                        className="gap-m"
                    />

                    <HeaderCashback totalCashback={totalCashback} />
                </React.Fragment>
            )}

            <InsurerLogo
                providerId={card.result.providerId}
                className={cx(["layout-insurer-logo"])}
                includesAboutInsurerModal
            />

            {/* Call to action */}

            {!hasBlockingError && (
                <React.Fragment>
                    <QuoteCallToAction
                        card={card}
                        groupId={groupId}
                        isDisabled={
                            hasBlockingError ||
                            !quoteState.isOverMinimumPremium ||
                            quoteState.isDisabled
                        }
                        url={generateApplicationURL(groupId, card)}
                        className="gap-l"
                        premium={totalPremium}
                        />
                    <div className="gap-ml">
                        {ClientProductTypesList.map((product, index) => {
                            if (!productList.includes(product)) return null;

                            return (
                                <span
                                    className={cx(
                                        "text-font-size-m",
                                        "text-primary",
                                        "text-semi-bold",
                                        "gap-m",
                                        "layout-flex-row--centered"
                                    )}
                                    key={index}>
                                    <Icon
                                        size="m"
                                        id="SaveTick"
                                        className={cx("space-ml")}
                                    />
                                    {getProductTypeAsString(product, true)} cover
                                </span>
                            );
                        })}
                    </div>
                </React.Fragment>
            )}

            <div className={cx(["layout-flex-s", "gap-ml", "gap-pre-xl"])}>
                {hasBlockingError ? (
                    <React.Fragment>
                        <QuoteResultError
                            isOpen={isOpen}
                            onClose={() => setIsOpen(false)}
                            errors={card.result.errors}
                        />
                        <span>
                            Insurer not available.
                            <a onClick={() => setIsOpen(true)}> Find out why.</a>
                        </span>
                    </React.Fragment>
                ) : (
                    <QuoteTableOffersAndBenefitsContent
                        hasInlineTitle
                        quoteState={quoteState}
                        cashbackAmount={card.totalCashback}
                        providerAddons={card.providerAddons}
                        providerId={card.result.providerId}
                        providerName={card.result.providerName}
                        specialOffers={card.specialOffers.map((s) => s.specialOfferName)}
                    />
                )}
            </div>

            {!hasBlockingError && (
                <React.Fragment>
                    {/* Mobile Handler */}
                    <Button
                        component="button"
                        size="s"
                        onClick={() => {
                            history.push({
                                pathname: `/result/card/${card.cardKey}`,
                                search: bannerHeadingText
                                    ? `?highlight=${bannerHeadingText}`
                                    : null
                            });
                        }}
                        variant="default"
                        className={cx("btn-full-width", "layout-hidden-desktop")}
                        isDisabled={quoteState.isDisabled}>
                        View&nbsp;details
                    </Button>
                    {/* Desktop Handler */}
                    <Button
                        component="button"
                        size="s"
                        onClick={() => setView(QuoteTableView.Details)}
                        variant="default"
                        className={cx("btn-full-width", "layout-hidden-mobile")}
                        isDisabled={quoteState.isDisabled}>
                        View&nbsp;details
                    </Button>
                </React.Fragment>
            )}
        </div>
    );
};

export default React.memo(QuoteTableOverviewCard);
